import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ShopifyBulkOperationStatusEnum,
  DataSourceStatus,
  GetDatasourceDocument,
  GetShopifyBulkOperationStatusDocument,
} from '@/lib/graphql/generated/graphql';
import { useQuery } from '@apollo/client/react';
import { Loader2 } from 'lucide-react';

const TERMINAL_STATUSES = new Set<ShopifyBulkOperationStatusEnum>([
  ShopifyBulkOperationStatusEnum.Completed,
  ShopifyBulkOperationStatusEnum.Failed,
  ShopifyBulkOperationStatusEnum.Canceled,
  ShopifyBulkOperationStatusEnum.Expired,
]);
const ERROR_STATUSES = new Set<ShopifyBulkOperationStatusEnum>([
  ShopifyBulkOperationStatusEnum.Failed,
  ShopifyBulkOperationStatusEnum.Canceled,
  ShopifyBulkOperationStatusEnum.Expired,
]);
interface ShopifyBulkProgressProps {
  dealId: string;
  dataSourceId?: string;
  onCompleted: (objectCount: number) => void;
  onError?: () => void;
}

export const ShopifyBulkProgress: React.FC<ShopifyBulkProgressProps> = ({
  dealId,
  dataSourceId,
  onCompleted,
  onError,
}) => {
  const { t } = useTranslation();

  const { data, stopPolling } = useQuery(
    GetShopifyBulkOperationStatusDocument,
    {
      variables: { dealId },
      pollInterval: 500,
      fetchPolicy: 'no-cache',
    }
  );

  // A Shopify bulk can finish before its products are persisted in Smart Offers.
  const {
    data: sourceData,
    error: sourceError,
    stopPolling: stopSourcePolling,
  } = useQuery(GetDatasourceDocument, {
    variables: { dealId },
    skip: !dataSourceId,
    pollInterval: 2_000,
    fetchPolicy: 'no-cache',
  });
  const source = sourceData?.deal?.dataSources.find(
    (source) => source.id === dataSourceId
  );
  const sourceStatus = source?.status;
  // Shopify stops reporting a finished operation, so its count can drop to 0
  // while the source is processed: report the products the source created.
  const createdCount =
    source && 'importSummary' in source
      ? source.importSummary?.itemsCreatedIds.length
      : undefined;
  const bulkStatus = data?.shopifyBulkOperationStatus;
  const objectCount = bulkStatus?.objectCount ?? 0;
  const isTerminal = bulkStatus
    ? TERMINAL_STATUSES.has(bulkStatus.status)
    : false;
  const isError = bulkStatus != null && ERROR_STATUSES.has(bulkStatus.status);
  // The operation exists once the mutation returns, so no reported operation
  // means Shopify is done: the wait is now for products to reach the offer.
  const addingProducts =
    !!dataSourceId && !!data && (bulkStatus == null || isTerminal);

  React.useEffect(() => {
    if (!data) return;
    if (isError || sourceError || sourceStatus === DataSourceStatus.Error) {
      stopPolling();
      stopSourcePolling();
      onError?.();
    } else if (
      (isTerminal || bulkStatus == null) &&
      (!dataSourceId || sourceStatus === DataSourceStatus.Processed)
    ) {
      stopPolling();
      stopSourcePolling();
      onCompleted(createdCount ?? objectCount);
    }
  }, [
    data,
    dataSourceId,
    sourceStatus,
    sourceError,
    stopSourcePolling,
    bulkStatus,
    isTerminal,
    isError,
    onCompleted,
    onError,
    stopPolling,
    objectCount,
    createdCount,
  ]);

  return (
    <div className="flex h-[400px] flex-col items-center justify-center gap-4">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
      <div className="text-center">
        {addingProducts ? (
          <p className="text-sm font-medium">
            {t('general.shopify_import_adding_products')}
          </p>
        ) : (
          <>
            <p className="text-sm font-medium">
              {t('general.shopify_import_in_progress')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('general.shopify_import_in_progress_with_count', {
                count: objectCount,
              })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
